import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Snowflake, Thermometer, Activity, Moon, Brain, Save, Heart, Droplets, Smile, TrendingUp, TrendingDown, Minus, CheckCircle2 } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";

const DEFAULT_FORM = {
  sleep_hours: 7.5, sleep_quality: 7, stress: 4, soreness: 3,
  hrv: 65, resting_hr: 55, mood: 7, hydration_l: 2.5,
  cold_plunge: false, sauna: false, cryotherapy: false, mobility: false,
  notes: "",
};

export default function Recovery() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [history, setHistory] = useState([]);
  const [insights, setInsights] = useState(null);
  const [windowDays, setWindowDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [h, i] = await Promise.all([
      api.get("/recovery"),
      api.get(`/recovery/insights?days=${windowDays}`),
    ]);
    setHistory(h.data);
    setInsights(i.data);
  };

  useEffect(() => { load(); }, [windowDays]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/recovery", form);
      window.dispatchEvent(new CustomEvent("recovery:logged"));
      toast.success("Recovery logged");
      await load();
    } catch {
      toast.error("Could not save");
    }
    setBusy(false);
  };

  const score = insights?.score ?? 70;
  const band = insights?.band || { band: "—", message: "Log today's recovery to get your readiness.", color: "#888" };

  return (
    <AppShell>
      <div data-testid="recovery-page">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">RECOVERY · READINESS</div>
            <h1 className="font-display text-4xl sm:text-5xl uppercase">Recovery <span className="text-[#d7ff00]">hub</span></h1>
          </div>
          <div className="flex gap-px bg-[#1f1f1f]" data-testid="window-toggle">
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => setWindowDays(d)}
                className={`px-4 py-2 font-mono-jp text-[10px] uppercase tracking-widest ${windowDays === d ? "bg-[#d7ff00] text-black" : "bg-[#0a0a0a] text-[#888] hover:text-white"}`}
                data-testid={`win-${d}`}>
                {d}D
              </button>
            ))}
          </div>
        </div>

        {/* Score hero */}
        <div className="mt-6 bg-[#0a0a0a] border border-[#1f1f1f] p-6 flex items-center gap-6 flex-wrap" data-testid="readiness-card">
          <div className="relative w-32 h-32 flex-shrink-0">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="44" stroke="#222" strokeWidth="6" fill="none" />
              <circle cx="50" cy="50" r="44" stroke={band.color} strokeWidth="6" fill="none"
                strokeDasharray={`${(score / 100) * 276} 276`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-mono-jp text-4xl tick" style={{ color: band.color }} data-testid="readiness-score">{score}</div>
              <div className="font-mono-jp text-[8px] uppercase tracking-widest text-[#888]">/100</div>
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="font-mono-jp text-[10px] uppercase tracking-widest" style={{ color: band.color }} data-testid="readiness-band">{band.band}</div>
            <p className="mt-2 text-[#ddd] text-sm max-w-md">{band.message}</p>
            {insights?.today_logged && (
              <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono-jp uppercase tracking-widest text-[#d7ff00]">
                <CheckCircle2 className="w-3 h-3" /> LOGGED TODAY · {insights.streak}-DAY STREAK
              </div>
            )}
          </div>
        </div>

        {/* Averages strip */}
        {insights && insights.log_count > 0 && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1f1f1f]" data-testid="averages-grid">
            <AvgTile label="HRV" value={insights.averages.hrv} unit="ms" trend={insights.trends.hrv_pct} icon={Activity} positiveIsUp />
            <AvgTile label="SLEEP" value={insights.averages.sleep_hours} unit="h" trend={insights.trends.sleep_pct} icon={Moon} positiveIsUp />
            <AvgTile label="RESTING HR" value={insights.averages.resting_hr} unit="bpm" trend={insights.trends.rhr_pct} icon={Heart} positiveIsUp={false} />
            <AvgTile label="STRESS" value={insights.averages.stress} unit="/10" icon={Brain} />
          </div>
        )}

        {/* Trend charts */}
        {insights && insights.log_count >= 3 && (
          <div className="mt-6 grid md:grid-cols-3 gap-4" data-testid="trend-charts">
            <TrendChart title="HRV TREND" unit="ms" data={insights.series.hrv} testid="chart-hrv" />
            <TrendChart title="SLEEP TREND" unit="h" data={insights.series.sleep_hours} testid="chart-sleep" />
            <TrendChart title="RESTING HR" unit="bpm" data={insights.series.resting_hr} testid="chart-rhr" />
          </div>
        )}

        {/* Habit frequency */}
        {insights && insights.log_count > 0 && (
          <div className="mt-6 bg-[#0a0a0a] border border-[#1f1f1f] p-5" data-testid="habits-card">
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">HABITS · LAST {windowDays}D</div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <HabitStat label="Cold Plunge" count={insights.habits.cold_plunge} icon={Snowflake} />
              <HabitStat label="Sauna" count={insights.habits.sauna} icon={Thermometer} />
              <HabitStat label="Cryo" count={insights.habits.cryotherapy} icon={Snowflake} />
              <HabitStat label="Mobility" count={insights.habits.mobility} icon={Activity} />
            </div>
          </div>
        )}

        {/* Log form */}
        <div className="mt-8 border-t border-[#1f1f1f] pt-8">
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">LOG TODAY</div>
          <h2 className="font-display text-3xl uppercase mt-1">How are you feeling?</h2>

          {/* Habit toggles */}
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { k: "cold_plunge", label: "Cold Plunge", Icon: Snowflake },
              { k: "cryotherapy", label: "Cryo", Icon: Snowflake },
              { k: "sauna", label: "Sauna", Icon: Thermometer },
              { k: "mobility", label: "Mobility", Icon: Activity },
            ].map(t => {
              const Icon = t.Icon;
              const active = form[t.k];
              return (
                <button key={t.k} onClick={() => setForm(d => ({ ...d, [t.k]: !active }))} data-testid={`toggle-${t.k}`}
                  className={`p-4 border ${active ? "border-[#d7ff00] text-[#d7ff00] bg-[#d7ff00]/5" : "border-[#222] text-[#bbb]"} flex flex-col items-start gap-2`}>
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest">{t.label}</div>
                </button>
              );
            })}
          </div>

          {/* Sliders */}
          <div className="mt-5 grid md:grid-cols-2 gap-4">
            <Range icon={Moon} label={`Sleep: ${form.sleep_hours}h`} testid="slide-sleep" min={4} max={10} step={0.5} value={form.sleep_hours} on={v => setForm({ ...form, sleep_hours: Number(v) })} />
            <Range icon={Moon} label={`Sleep quality: ${form.sleep_quality}/10`} testid="slide-sleepq" min={1} max={10} value={form.sleep_quality} on={v => setForm({ ...form, sleep_quality: Number(v) })} />
            <Range icon={Activity} label={`HRV: ${form.hrv} ms`} testid="slide-hrv" min={20} max={150} value={form.hrv} on={v => setForm({ ...form, hrv: Number(v) })} />
            <Range icon={Heart} label={`Resting HR: ${form.resting_hr} bpm`} testid="slide-rhr" min={35} max={90} value={form.resting_hr} on={v => setForm({ ...form, resting_hr: Number(v) })} />
            <Range icon={Brain} label={`Stress: ${form.stress}/10`} testid="slide-stress" min={1} max={10} value={form.stress} on={v => setForm({ ...form, stress: Number(v) })} />
            <Range icon={Activity} label={`Soreness: ${form.soreness}/10`} testid="slide-soreness" min={1} max={10} value={form.soreness} on={v => setForm({ ...form, soreness: Number(v) })} />
            <Range icon={Smile} label={`Mood: ${form.mood}/10`} testid="slide-mood" min={1} max={10} value={form.mood} on={v => setForm({ ...form, mood: Number(v) })} />
            <Range icon={Droplets} label={`Hydration: ${form.hydration_l}L`} testid="slide-hydration" min={0.5} max={5} step={0.1} value={form.hydration_l} on={v => setForm({ ...form, hydration_l: Number(v) })} />
          </div>

          <textarea rows={3} placeholder="Notes — niggles, motivation, anything Coach JP should know..." value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
            className="mt-4 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2 text-sm" data-testid="recovery-notes" />
          <button onClick={submit} disabled={busy} className="mt-4 bg-[#d7ff00] text-black font-display uppercase tracking-wider px-8 py-3 hover:bg-white disabled:opacity-50 flex items-center gap-2" data-testid="log-recovery-btn">
            <Save className="w-4 h-4" /> {busy ? "Saving..." : "Log Recovery"}
          </button>
        </div>

        {/* Recent logs */}
        {history.length > 0 && (
          <div className="mt-8">
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">RECENT LOGS</div>
            <div className="mt-3 space-y-2" data-testid="recovery-history">
              {history.slice(0, 7).map(h => (
                <div key={h.id} className="bg-[#0a0a0a] border border-[#1f1f1f] px-4 py-3 text-xs flex flex-wrap items-center justify-between gap-3 font-mono-jp text-[#bbb]">
                  <span className="text-white">{new Date(h.date).toLocaleDateString(undefined, { day: "2-digit", month: "short" }).toUpperCase()}</span>
                  <span>{h.sleep_hours ? `${h.sleep_hours}H SLEEP` : ""}{h.hrv ? ` · HRV ${h.hrv}` : ""}{h.resting_hr ? ` · RHR ${h.resting_hr}` : ""}{h.stress ? ` · STRESS ${h.stress}` : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Range({ icon: Icon, label, testid, min, max, step = 1, value, on }) {
  return (
    <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-4">
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center gap-2"><Icon className="w-3 h-3 text-[#d7ff00]" /> {label}</div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => on(e.target.value)} className="w-full mt-2 accent-[#d7ff00]" data-testid={testid} />
    </div>
  );
}

function AvgTile({ label, value, unit, trend, icon: Icon, positiveIsUp = true }) {
  const goodTrend = trend !== null && trend !== undefined
    ? (positiveIsUp ? trend > 0 : trend < 0)
    : null;
  const TrendIcon = trend === null || trend === undefined
    ? Minus
    : (trend > 0 ? TrendingUp : trend < 0 ? TrendingDown : Minus);
  const trendColor = goodTrend === null ? "#666" : goodTrend ? "#d7ff00" : "#ff8800";
  return (
    <div className="bg-[#0a0a0a] p-4">
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-[#d7ff00]" /> {label}
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="font-mono-jp text-3xl text-[#d7ff00] tick">{value ?? "—"}</span>
        <span className="font-mono-jp text-xs text-[#888]">{value !== null && value !== undefined ? unit : ""}</span>
      </div>
      {trend !== null && trend !== undefined && (
        <div className="mt-1 flex items-center gap-1 text-[10px] font-mono-jp uppercase tracking-widest" style={{ color: trendColor }}>
          <TrendIcon className="w-3 h-3" /> {trend > 0 ? "+" : ""}{trend}%
        </div>
      )}
    </div>
  );
}

function TrendChart({ title, unit, data, testid }) {
  if (!data || data.length < 2) {
    return (
      <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-4" data-testid={testid}>
        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">{title}</div>
        <div className="h-32 mt-3 flex items-center justify-center text-[#444] font-mono-jp text-[10px] uppercase tracking-widest">Not enough data</div>
      </div>
    );
  }
  const chartData = data.map(d => ({ x: d.date, y: d.value }));
  return (
    <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-4" data-testid={testid}>
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">{title} · {unit}</div>
      <div className="h-32 mt-3">
        <ResponsiveContainer>
          <LineChart data={chartData}>
            <XAxis dataKey="x" hide />
            <YAxis stroke="#444" tick={{ fontFamily: "Space Mono", fontSize: 9, fill: "#666" }} width={28} domain={["dataMin - 2", "dataMax + 2"]} />
            <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #d7ff0040", fontSize: 11 }} labelStyle={{ color: "#888" }} />
            <Line type="monotone" dataKey="y" stroke="#d7ff00" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function HabitStat({ label, count, icon: Icon }) {
  return (
    <div className="border border-[#1f1f1f] p-3 flex items-center gap-3">
      <Icon className={`w-4 h-4 ${count > 0 ? "text-[#d7ff00]" : "text-[#444]"}`} strokeWidth={1.5} />
      <div>
        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">{label}</div>
        <div className="font-display text-xl text-white">{count} <span className="text-[#666] text-xs">sessions</span></div>
      </div>
    </div>
  );
}
